/**
 * SKILLCARD à gagner d'un Live School : créature origami dans la teinte de la
 * carte, titre, étoiles de rareté, taille du podium. Ouvre la page de la carte.
 * Partagée par l'onglet Live School (chaque live de chaque niveau) et le player.
 */

import { Gift } from 'lucide-react';
import { cn, MathText } from '@jeraisonne/ui';
import { OrigamiCreature } from '@jeraisonne/ui/tool-cards';
import {
  CARD_RARITY_LABELS,
  CARD_RARITY_STARS,
  cardPalette,
  type CardColorTheme,
  type CardRarity,
} from '@jeraisonne/db/cards';

export interface LivePrizeTileCard {
  card_id: string;
  title: string;
  rarity: string;
  top_n: number;
  color_theme?: string | null;
  creature?: unknown;
  levels?: string[];
}

/** « Le 1er gagne » / « Le top 3 gagne ». */
export function prizeWinnersLabel(topN: number): string {
  return topN === 1 ? 'Le 1er gagne' : `Le top ${topN} gagne`;
}

export function LivePrizeTile({
  prize,
  compact = false,
  tone = 'default',
  className,
}: {
  prize: LivePrizeTileCard;
  compact?: boolean;
  /** `glass` : posée sur une cover (carte d'un live, écran du live). */
  tone?: 'default' | 'glass';
  className?: string;
}) {
  const glass = tone === 'glass';
  const rarity = (prize.rarity in CARD_RARITY_STARS ? prize.rarity : 'simple') as CardRarity;
  const stars = CARD_RARITY_STARS[rarity];
  const palette = cardPalette(prize.levels ?? [], (prize.color_theme ?? 'level') as CardColorTheme);
  return (
    <a
      href={`/card/${prize.card_id}`}
      className={cn(
        'flex min-h-11 items-center gap-3 rounded-2xl border active:scale-[0.98]',
        glass
          ? 'border-[#f4c64e]/60 bg-black/35 text-white backdrop-blur'
          : 'border-accent-300 bg-accent-50 ocean:bg-accent-500/10',
        compact ? 'p-2' : 'p-3',
        className,
      )}
    >
      <span
        aria-hidden
        className={cn(
          'flex shrink-0 items-center justify-center rounded-xl border-2',
          compact ? 'h-12 w-12' : 'h-16 w-16',
        )}
        style={{ background: palette.bg, borderColor: palette.accent }}
      >
        <OrigamiCreature
          creature={prize.creature ?? null}
          accent={palette.accent}
          className={compact ? 'h-9 w-9' : 'h-12 w-12'}
        />
      </span>
      <span className="min-w-0 flex-1 text-sm">
        <span
          className={cn(
            'flex items-center gap-1 text-xs font-semibold',
            glass ? 'text-[#f4c64e]' : 'text-accent-700',
          )}
        >
          <Gift className="h-3.5 w-3.5 shrink-0" aria-hidden /> {prizeWinnersLabel(prize.top_n)}
        </span>
        <span className={cn('block truncate font-semibold', glass ? 'text-white' : 'text-ink-900')}>
          <MathText>{prize.title}</MathText>
        </span>
        <span
          className="text-xs tracking-wider text-amber-500"
          aria-label={`Rareté : ${CARD_RARITY_LABELS[rarity]}`}
        >
          {'★'.repeat(stars)}
          <span className={glass ? 'text-white/25' : 'text-ink-200'}>{'★'.repeat(5 - stars)}</span>
        </span>
      </span>
    </a>
  );
}
